import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { logout as logoutAPI } from '@/api/auth/logout';
import { getMe } from '@/api/auth/me';
import { buildLogoutUrl } from '@/api/auth/oidcConfig';

export interface User {
  phone?: string;
  initials: string;
  id: string;
  name: string;
  /** A human name to show in the UI: name -> "first last" -> email prefix ->
   *  "User". Use this in sidebars instead of `name || 'User'`. Optional on input
   *  (setUser/AppContext normalize it), always populated once in context. */
  displayName?: string;
  email: string;
  role: 'admin' | 'organization' | 'operator' | 'end-user';
  firstName?: string;
  lastName?: string;
  emailVerified?: boolean;
  avatar?: string;
  country?: string;
  requiresTwoFactor?: boolean;
}

/**
 * Authoritative session state. The route guards read this, never localStorage, so
 * a user editing localStorage can no longer flip their own role.
 * - 'loading'        : fetching /auth/me; guards should wait.
 * - 'authenticated'  : the server confirmed a session (cookie-based).
 * - 'unauthenticated': no valid server session.
 */
export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AppContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  user: User | null;
  setUser: (user: User | null) => void;
  isAuthenticated: boolean;
  /** Status of the server-verified session. Guards read this, not localStorage. */
  sessionStatus: SessionStatus;
  logout: () => Promise<void>;
  isLoggingOut: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

/** Normalize the /auth/me user payload into the in-memory User shape. */
function mapMeUser(u: NonNullable<Awaited<ReturnType<typeof getMe>>['data']>['user']): User {
  const firstName = u.profile?.firstName;
  const lastName = u.profile?.lastName;
  const initials =
    firstName && lastName
      ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
      : (firstName?.substring(0, 2).toUpperCase() || u.username?.substring(0, 2).toUpperCase() || 'U');
  return {
    id: u._id,
    name: u.username || `${firstName || ''} ${lastName || ''}`.trim(),
    email: u.email,
    role: u.role as User['role'],
    firstName,
    lastName,
    phone: u.profile?.phone,
    avatar: u.profile?.avatar,
    emailVerified: u.isVerified,
    requiresTwoFactor: u.twoFactorEnabled,
    initials,
  };
}

export function AppProvider({ children }: { children: ReactNode }) {
  
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as 'light' | 'dark') || 'dark';
  });

  const [user, setUserState] = useState<User | null>(() => {
    const saved = localStorage.getItem('user');
    
    if (saved) {
      try {
        const parsedUser = JSON.parse(saved);
        
        // OAuth users have emailVerified=true, so this should pass
        if (parsedUser.emailVerified === false) {
          localStorage.removeItem('user');
          return null;
        }
        
        const firstName = parsedUser.firstName || parsedUser.profile?.firstName;
        const lastName = parsedUser.lastName || parsedUser.profile?.lastName;
        const resolvedName =
          parsedUser.name ||
          [firstName, lastName].filter(Boolean).join(' ').trim() ||
          parsedUser.username ||
          parsedUser.email?.split('@')[0] ||
          '';
        const resolvedInitials =
          parsedUser.initials ||
          (firstName && lastName
            ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
            : firstName?.substring(0, 2).toUpperCase()) ||
          resolvedName?.substring(0, 2).toUpperCase() ||
          'U';

        const userData = {
          ...parsedUser,
          id: parsedUser._id || parsedUser.id,
          name: resolvedName,
          // Never blank: prefer the resolved name / first+last, else email prefix,
          // else "User". Sidebars read this so they never show a bare "User" when
          // a name exists.
          displayName:
            resolvedName ||
            [firstName, lastName].filter(Boolean).join(' ').trim() ||
            parsedUser.email?.split('@')[0] ||
            'User',
          email: parsedUser.email,
          role: parsedUser.role,
          phone: parsedUser.profile?.phone || parsedUser.phone,
          initials: resolvedInitials,
          firstName,
          lastName,
          emailVerified: parsedUser.emailVerified,
          avatar: parsedUser.profile?.avatar || parsedUser.avatar || parsedUser.profileImage,
          country: parsedUser.profile?.country || parsedUser.country || parsedUser.location || parsedUser.profile?.location,
        };
        
        return userData;
      } catch (error: unknown) {
        console.error('Error parsing user from localStorage:', error);
        localStorage.removeItem('user');
        return null;
      }
    }
    
    return null;
  });

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Session is verified against the server (cookie-based /auth/me), held in
  // memory. Seed as 'loading' only when there's a cached user to validate.
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>(() =>
    localStorage.getItem('user') ? 'loading' : 'unauthenticated',
  );

  useEffect(() => {
    const root = document.documentElement;
    
    localStorage.setItem('theme', theme);
    
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  // Derive the authoritative session from the server. We ask /auth/me
  // (authenticated by the HttpOnly cookie) and hold the result in memory. The
  // role here — not the editable localStorage blob — is what the route guards
  // consult. A 401 clears the in-memory session.
  useEffect(() => {
    let alive = true;
    if (!localStorage.getItem('user')) {
      setSessionStatus('unauthenticated');
      return;
    }
    (async () => {
      try {
        const res = await getMe();
        if (!alive) return;
        const u = res?.data?.user;
        if (res?.success && u) {
          setUserState(mapMeUser(u));
          setSessionStatus('authenticated');
        } else {
          setUserState(null);
          localStorage.removeItem('user');
          setSessionStatus('unauthenticated');
        }
      } catch {
        if (!alive) return;
        setUserState(null);
        localStorage.removeItem('user');
        setSessionStatus('unauthenticated');
      }
    })();
    return () => {
      alive = false;
    };
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update localStorage when user changes.
  // Wrapped in useCallback so its identity is stable across renders — consumers
  // such as AuthCallback list setUser in an effect dependency array, and an
  // unstable identity re-ran that effect on every user update (KX-3).
  const setUser = useCallback((user: User | null) => {
    // Ensure displayName is always populated, even when callers (e.g. the auth
    // callback) build the user object themselves.
    if (user && !user.displayName) {
      user = {
        ...user,
        displayName:
          user.name ||
          [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
          user.email?.split('@')[0] ||
          'User',
      };
    }
    setUserState(user);
    // Keep session status in step with explicit logins/logouts (e.g. the auth
    // callback) so guards react immediately without waiting for /auth/me.
    setSessionStatus(user ? 'authenticated' : 'unauthenticated');
    
    if (user) {
      // Only save if emailVerified is not explicitly false
      if (user.emailVerified !== false) {
        localStorage.setItem('user', JSON.stringify(user));
      }
    } else {
      localStorage.removeItem('user');
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoggingOut(true);

      // End our backend session (clears the app's session cookie).
      await logoutAPI();
    } catch (error: unknown) {
      console.error('Logout API error:', error);
    } finally {
      // Clear only non-sensitive client state. IMPORTANT: do NOT flip React auth
      // state (setUser(null)) before the redirect — doing so makes the route
      // guards fire a client-side <Navigate> that cancels the full-page redirect
      // below, so the browser never reaches the Auth0 logout and the IdP SSO
      // session survives (next login silently re-authenticates).
      localStorage.removeItem('user');
      
      setIsLoggingOut(false);
      
      // Provider-portable logout via our BFF (no vendor literal here).
      window.location.href = buildLogoutUrl();
    }
  }, []);

  const isAuthenticated = !!user;

  const contextValue = useMemo<AppContextType>(
    () => ({
      theme,
      toggleTheme,
      user,
      setUser,
      isAuthenticated,
      sessionStatus,
      logout,
      isLoggingOut,
    }),
    [theme, toggleTheme, user, setUser, isAuthenticated, sessionStatus, logout, isLoggingOut],
  );

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
